/**
 * Every piece of catalog artwork in the app goes through here.
 *
 * ## The defect this replaces
 *
 * Four files carried the same copy-pasted `<img>`, and all four had the same bug:
 *
 *     {art ? <img src={art} onError={(e) => e.currentTarget.remove()} /> : <span>AB</span>}
 *
 * The handler **removes the element**. The ternary has already resolved by then, so React
 * never renders the `<span>` — a dead provider URL leaves an empty box rather than the
 * two-letter placeholder it was written to fall back to. Provider artwork dies constantly;
 * this was the common case, not the edge case.
 *
 * ## Why the placeholder is a colour and not a grey box
 *
 * A grid of identical grey rectangles gives the eye nothing to hold on to while artwork
 * loads, and every card looks like the same missing thing. The tint here is derived from
 * the item's own id, so it is stable across renders and across sessions — the same film is
 * always the same colour — and a wall of loading cards reads as a wall of *different*
 * cards. It costs one hash of a short string and no network request at all.
 *
 * When the server starts extracting real accent colours from posters (§24 of the plan),
 * `tint` takes them instead and this becomes a genuine low-quality image placeholder. The
 * derivation is deliberately isolated in `tintFor` so that is a one-function change.
 *
 * ## Layout stability
 *
 * The aspect ratio is on the wrapper, not the image, so the slot exists at its final size
 * before anything has loaded. That is most of the app's cumulative layout shift: without
 * it a rail of ten posters reflows ten times as they arrive.
 */
import { useState } from 'react';

export type MediaShape = 'poster' | 'wide' | 'hero';

export interface MediaImageProps {
  src: string | null | undefined;
  /**
   * Empty by default, and that is correct for a card: the title is rendered as text
   * directly beneath, so alt text here makes a screen reader read every title twice.
   */
  alt?: string;
  shape: MediaShape;
  /**
   * Shown when there is no image, or the image fails. Trimmed to two characters.
   *
   * Omit it where the surrounding layout already names the item — the hero prints the
   * title in display type across the same box, and two initials beside it is the same
   * word twice at two sizes.
   */
  fallback?: string;
  /** Stable input for the placeholder tint. The item id, ideally — it never changes. */
  seed: string;
  /**
   * Above the fold. Drops `loading="lazy"` and asks the browser to fetch it early.
   *
   * Off by default: a hero and the first few cards of the first rail want it, and a grid
   * of forty posters emphatically does not — marking everything high priority is the same
   * as marking nothing.
   */
  priority?: boolean;
}

/**
 * A stable, dark tint from an arbitrary string.
 *
 * FNV-1a, because it is four lines and has good avalanche for short ASCII keys — an id and
 * the same id with one character changed land far apart, which is the whole point. The
 * hash is only ever used to pick a hue, so its cryptographic properties are irrelevant.
 *
 * Lightness and saturation are fixed and low. The placeholder must sit *behind* artwork
 * and beneath white text without competing with either, so only the hue varies.
 */
export function tintFor(seed: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  const hue = Math.abs(h) % 360;
  return `linear-gradient(150deg, hsl(${hue} 26% 17%), hsl(${(hue + 38) % 360} 22% 11%))`;
}

export function MediaImage({
  src,
  alt = '',
  shape,
  fallback,
  seed,
  priority = false,
}: MediaImageProps) {
  /**
   * `failed` covers both "no URL at all" and "the URL did not load", because the rendered
   * result is identical and splitting them produced the bug above — one branch chosen at
   * render time and the other only reachable after it.
   */
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const show = Boolean(src) && !failed;

  return (
    <div
      className={`media media--${shape}`}
      style={{ backgroundImage: tintFor(seed) }}
      data-loaded={loaded ? '' : undefined}
    >
      {show ? (
        <img
          className="media__img"
          src={src ?? undefined}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={priority ? 'high' : 'auto'}
          onLoad={() => setLoaded(true)}
          // Sets state instead of removing the node, so the placeholder below actually
          // gets its turn.
          onError={() => setFailed(true)}
        />
      ) : (
        fallback && <span className="media__fallback">{fallback.slice(0, 2).toUpperCase()}</span>
      )}
    </div>
  );
}
